// Input.jsx
import { useField } from 'formik'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons'
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons'
import { CFieldProps } from '@velocity/shared'
import { Tooltip } from '../Tooltip'
import dayjs from 'dayjs'

export const CInput = ({
  label,
  placeholder,
  className = '',
  required,
  initialValue,
  fieldProps,
  helpText,
  ...props
}: CFieldProps) => {
  const { t } = useTranslation()
  const [field, meta] = useField(props.name)
  const [hidePassword, setHidePassword] = useState(true)

  const handleTogglePassword = () => setHidePassword(!hidePassword)
  const value = useMemo(() => {
    if (props.type === 'datetime-local') {
      return field.value ? dayjs(field.value).format('YYYY-MM-DDTHH:mm') : ''
    }
    if (props.type === 'date') {
      return field.value ? dayjs(field.value).format('YYYY-MM-DD') : ''
    }
    return field.value
  }, [field.value, props.type])
  return (
    <div>
      <input
        {...props}
        {...fieldProps}
        {...field}
        id={props.name}
        value={value}
        placeholder={placeholder ? t(placeholder) : ''}
        className={`p-1.5 border rounded-md w-full rounded-md ${props.disabled ? 'bg-[#EEEEEE]' : ''} ${
          meta?.touched && meta?.error ? 'border-red-500' : 'border-gray-300'
        }`}
        type={
          props.type === 'currency' ? 'number'
          : props.type === 'password' ?
            hidePassword ?
              'password'
            : 'text'
          : props.type || 'text'
        }
      />
      {props.type === 'password' && (
        <button
          type='button'
          className={`absolute inset-y-2 text-gray-900 right-0 pt-5 p-2 focus:outline-none ${meta.touched && meta.error ? 'text-[#F88078]' : ''}`}
          onClick={handleTogglePassword}
        >
          {hidePassword ?
            <FontAwesomeIcon icon={faEye} />
          : <FontAwesomeIcon icon={faEyeSlash} />}
        </button>
      )}
    </div>
  )
}
export const Label = (props: CFieldProps) => {
  const { t } = useTranslation()
  return props.label ?
      <label
        htmlFor={props.name}
        className='flex items-center gap-1.5 text-sm text-gray-700'
      >
        <span className='flex items-center'>
          {typeof props.label === 'string' ? t(props.label) : props.label}
          {props.required && <span className='text-danger'>*</span>}
        </span>
        {props.helpText && (
          <Tooltip title={props.helpText} html={props.helpTextAsHtml}>
            <FontAwesomeIcon
              icon={faCircleQuestion}
              className='text-gray-400 hover:text-gray-600 cursor-help text-xs'
            />
          </Tooltip>
        )}
      </label>
    : null
}

export const Error = ({ name }: { name: string }) => {
  const [, meta] = useField(name)
  const { t } = useTranslation()
  return meta.touched && meta.error ?
      <div className='text-red-500 text-sm'>{t(meta.error)}</div>
    : null
}
